import {groupBy} from '@benchling/js-utils';
import {Stack} from '@mantine/core';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {ClientTagSchemaConstraint} from 'constants/RegistryConstants';
import {
  FieldDefinitionType,
  TagSchemaItemType,
  AntibodyComponentSchemaRole,
  AntibodyRelatedSchemaRole,
  TypeSpecificConstraintType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useSkippableBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {fieldDefinitionTypeToFieldType} from 'schemas/fields/FieldDefinitionCompatibilityUtils';
import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {SettingsLabel} from 'ui-library/SettingsField';
import Spinner from 'ui-library/Spinner';

import {
  AntibodyComponentSchemaConfigurationQuery,
  AntibodyComponentSchemaConfigurationQuery_antibodyEnforcedSchemaSpecs_AntibodyEnforcedComponentSchemaSpecNode_enforcedConstraintSpec_AntibodyEnforcedConstraintSpecNode as AntibodyEnforcedConstraintSpecNode,
  AntibodyComponentSchemaConfigurationQuery_antibodyEnforcedSchemaSpecs_AntibodyEnforcedComponentSchemaSpecNode_enforcedFieldSpecs_AntibodyEnforcedSchemaFieldSpecNode as AntibodyEnforcedSchemaFieldSpecNode,
  AntibodyComponentSchemaConfigurationQuery_antibodyEnforcedSchemaSpecs_AntibodyEnforcedRelatedSchemaSpecNode as AntibodyEnforcedRelatedSchemaSpecNode,
  AntibodyComponentSchemaConfigurationQuery_antibodyEnforcedSchemaSpecs_AntibodyEnforcedComponentSchemaSpecNode as AntibodyEnforcedComponentSchemaSpecNode,
} from './__generated/AntibodyComponentSchemaConfigurationQuery';
import {
  AntibodyComponentSchemaInterfaceApiId,
  isAntibodyComponentSchemaInterfaceId,
} from './AntibodySchemaInterfaceConstants';

export type AntibodyEnforcedSchemaFieldSpec = AntibodyEnforcedSchemaFieldSpecNode;

export type AntibodyEnforcedSchemaSpec =
  | AntibodyEnforcedRelatedSchemaSpecNode
  | AntibodyEnforcedComponentSchemaSpecNode;

export type AntibodyEnforcedConstraintSpec = AntibodyEnforcedConstraintSpecNode;
export type AntibodyComponentSchemaOption =
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID
  | null;

export type AntibodyComponentSchemaItemType =
  | TagSchemaItemType.AA_SEQUENCE
  | TagSchemaItemType.DNA_SEQUENCE
  | TagSchemaItemType.BASIC_FOLDER_ITEM
  | TagSchemaItemType.MOLECULE;

export const ANTIBODY_COMPONENT_SCHEMA_ITEM_TYPES: ReadonlyArray<AntibodyComponentSchemaItemType> = [
  TagSchemaItemType.AA_SEQUENCE,
  TagSchemaItemType.DNA_SEQUENCE,
  TagSchemaItemType.BASIC_FOLDER_ITEM,
  TagSchemaItemType.MOLECULE,
];

export function isAntibodyComponentSchemaItemType(
  itemType: TagSchemaItemType | null
): itemType is AntibodyComponentSchemaItemType {
  return (
    itemType != null &&
    ANTIBODY_COMPONENT_SCHEMA_ITEM_TYPES.includes(itemType as AntibodyComponentSchemaItemType)
  );
}

const ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE: Record<
  AntibodyComponentSchemaInterfaceApiId,
  AntibodyComponentSchemaRole | AntibodyRelatedSchemaRole
> = {
  [AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID]: AntibodyComponentSchemaRole.AA_DOMAIN,
  [AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID]: AntibodyComponentSchemaRole.AA_CHAIN,
  [AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID]: AntibodyRelatedSchemaRole.DNA_DOMAIN,
  [AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID]: AntibodyRelatedSchemaRole.DNA_CHAIN,
  [AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID]: AntibodyRelatedSchemaRole.AA_PAIR,
  [AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID]: AntibodyRelatedSchemaRole.DNA_PAIR,
  [AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID]: AntibodyRelatedSchemaRole.ANTIGEN,
};

const ANTIBODY_SCHEMA_ROLE_TO_INTERFACE_ID: Record<
  AntibodyComponentSchemaRole | AntibodyRelatedSchemaRole,
  AntibodyComponentSchemaInterfaceApiId
> = {
  [AntibodyRelatedSchemaRole.DNA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
  [AntibodyRelatedSchemaRole.DNA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID,
  [AntibodyRelatedSchemaRole.AA_PAIR]: AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID,
  [AntibodyRelatedSchemaRole.DNA_PAIR]: AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID,
  [AntibodyRelatedSchemaRole.ANTIGEN]: AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
  [AntibodyComponentSchemaRole.AA_CHAIN]: AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
  [AntibodyComponentSchemaRole.AA_DOMAIN]: AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
};

export interface QueryingAntibodyComponentSchemaConfigurationProps {
  disabled: boolean;
  itemType: AntibodyComponentSchemaItemType;
  onChange: (value: AntibodyComponentSchemaOption, schemaSpec: AntibodyEnforcedSchemaSpec | null) => void;
  value: AntibodyComponentSchemaOption;
}

export interface AntibodyComponentSchemaConfigurationProps extends QueryingAntibodyComponentSchemaConfigurationProps {
  schemaSpecs: Array<AntibodyEnforcedSchemaSpec>;
}

const ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME: Record<
  AntibodyComponentSchemaInterfaceApiId,
  string
> = {
  [AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID]: 'AA domain',
  [AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID]: 'AA chain',
  [AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID]: 'DNA domain',
  [AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID]: 'DNA chain',
  [AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID]: 'AA variable pair',
  [AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID]: 'DNA variable pair',
  [AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID]: 'Antigen',
};

export const ITEM_TYPE_TO_INTERFACE_IDS: Record<
  AntibodyComponentSchemaItemType,
  Array<AntibodyComponentSchemaInterfaceApiId>
> = {
  [TagSchemaItemType.AA_SEQUENCE]: [
    AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
    AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
    AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
  ],
  [TagSchemaItemType.DNA_SEQUENCE]: [
    AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
    AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID,
  ],
  [TagSchemaItemType.BASIC_FOLDER_ITEM]: [
    AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID,
    AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID,
    AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
  ],
  [TagSchemaItemType.MOLECULE]: [AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID],
};

export function isAntibodyComponentSchemaInterfaceIdValidForItemType(
  interfaceId: string,
  itemType: TagSchemaItemType | null
): boolean {
  return (
    itemType != null &&
    isAntibodyComponentSchemaInterfaceId(interfaceId) &&
    isAntibodyComponentSchemaItemType(itemType) &&
    ITEM_TYPE_TO_INTERFACE_IDS[itemType].includes(interfaceId)
  );
}

function getOptionsForItemType(itemType: AntibodyComponentSchemaItemType): Array<OptionData> {
  const interfaceIds = ITEM_TYPE_TO_INTERFACE_IDS[itemType];
  return [
    {name: 'None', value: null},
    ...interfaceIds.map((id) => ({
      name: ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[id],
      value: id,
    })),
  ];
}

function enforcedFieldSpecToField(
  interfaceId: AntibodyComponentSchemaInterfaceApiId,
  enforcedFieldSpec: AntibodyEnforcedSchemaFieldSpec,
  registryId: string
): ClientSchemaField {
  const compatibleDropdowns = enforcedFieldSpec.dropdown?.compatibleDropdowns;
  const validDropdown =
    compatibleDropdowns != null
      ? compatibleDropdowns.find((dropdown) => dropdown.registry.id === registryId)
      : null;
  // Should be caught in the caller, but assert here just in case.
  assert(
    !(enforcedFieldSpec.fieldType === FieldDefinitionType.DROPDOWN && validDropdown == null),
    'No compatible dropdown definition found'
  );
  const dropdown = validDropdown ?? null;
  return {
    cid: uuidv4(),
    name: enforcedFieldSpec.fieldName,
    systemName: enforcedFieldSpec.systemName,
    displayName: enforcedFieldSpec.fieldName,
    fieldType: fieldDefinitionTypeToFieldType(enforcedFieldSpec.fieldType),
    isRequired: enforcedFieldSpec.isRequired,
    isMulti: enforcedFieldSpec.isMulti,
    isComputed: false,
    isConvertedFromLink: false,
    isSnapshot: false,
    isIntegration: false,
    isParentLink: false,
    requiredLink:
      enforcedFieldSpec.linkedInterface == null
        ? null
        : {
            tagSchema: null,
            schemaInterface: {
              id: enforcedFieldSpec.linkedInterface.id,
              name: enforcedFieldSpec.linkedInterface.name,
            },
            folderItemType: null,
            entitySchemaFieldPath: null,
          },
    schemaFieldSelectorId: dropdown?.id ?? null,
    archiveRecord: null,
    unitApiIdentifier: enforcedFieldSpec.unit?.id ?? null,
    appliedFieldset: {
      id: interfaceId,
      name: ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[interfaceId],
    },
  };
}

function shouldRemoveField(
  field: ClientSchemaField,
  appliedInterfaceId: AntibodyComponentSchemaInterfaceApiId | null
): boolean {
  return (
    // The field was added by an interface.
    field.appliedFieldset?.id != null &&
    // The field was not added by the currently applied interface.
    field.appliedFieldset.id !== appliedInterfaceId &&
    // The field was added by another interface that is an antibody component schema interface.
    isAntibodyComponentSchemaInterfaceId(field.appliedFieldset.id)
  );
}

export function shouldRemoveAntibodyEnforcedFields(
  schemaId: string | null,
  itemType: TagSchemaItemType | null,
  interfaceData: Array<ImplementedStaticSchemaInterface>
): boolean {
  const maybeAntibodyComponentSchemaInterface = interfaceData.find((interfaceData) =>
    isAntibodyComponentSchemaInterfaceId(interfaceData.interfaceId)
  );
  return (
    // Only remove fields if we are creating a new schema - otherwise the fields might have data!
    schemaId == null &&
    // Only remove fields if we don't have an antibody component schema interface
    (maybeAntibodyComponentSchemaInterface == null ||
      // Or the interface is not valid for the item type
      !isAntibodyComponentSchemaInterfaceIdValidForItemType(
        maybeAntibodyComponentSchemaInterface.interfaceId,
        itemType
      ))
  );
}

export function removeAntibodyEnforcedFields(
  existingFields: Array<ClientSchemaField>
): Array<ClientSchemaField> {
  return existingFields.filter((field) => !shouldRemoveField(field, null));
}

function validateDropdownFieldSpecs(
  schemaSpec: AntibodyEnforcedSchemaSpec | null,
  registryId: string
): boolean {
  const enforcedFieldSpecs = schemaSpec?.enforcedFieldSpecs ?? [];
  const dropdownFieldSpecsWithoutCompatibleDropdowns = enforcedFieldSpecs.filter(
    (spec) =>
      spec.fieldType === FieldDefinitionType.DROPDOWN &&
      (spec.dropdown?.compatibleDropdowns == null ||
        spec.dropdown.compatibleDropdowns.length === 0 ||
        spec.dropdown.compatibleDropdowns.every((dropdown) => dropdown.registry.id !== registryId))
  );
  return dropdownFieldSpecsWithoutCompatibleDropdowns.length === 0;
}

function constraintSpecToClientTagSchemaConstraint(
  constraintSpec: AntibodyEnforcedConstraintSpec,
  schemaFields: Array<ClientSchemaField>
): ClientTagSchemaConstraint {
  const fieldsByName: Record<string, Array<ClientSchemaField>> = groupBy(schemaFields, (field) => field.name);
  assert(
    constraintSpec.enforcedMinimumFieldDefinitionNames.every(
      (fieldName) => fieldsByName[fieldName] != null && fieldsByName[fieldName].length > 0
    ),
    'Expected all minimum unique fields to be present in the schema fields'
  );
  return {
    areUniqueResiduesCaseSensitive: constraintSpec.enforcedTypeSpecificConstraint?.isCaseSensitive ?? false,
    uniqueResidues:
      constraintSpec.enforcedTypeSpecificConstraint?.constraintType ===
      TypeSpecificConstraintType.UNIQUE_RESIDUES,
    uniqueCanonicalSmiles:
      constraintSpec.enforcedTypeSpecificConstraint?.constraintType ===
      TypeSpecificConstraintType.UNIQUE_CANONICAL_SMILES,
    fields: constraintSpec.enforcedMinimumFieldDefinitionNames.map((fieldName) => fieldsByName[fieldName][0]),
  };
}

/**
 * Clears any fields from the existing constraint that are not present in the updated schema fields.
 *
 * This is necessary because when we deselect an antibody component role, we need to clear any fields that
 * were added by that role from the constraint.
 */
export function clearInvalidFieldsFromExistingConstraint(
  existingConstraint: ClientTagSchemaConstraint,
  updatedSchemaFields: Array<ClientSchemaField>
): ClientTagSchemaConstraint {
  const validFieldNames = new Set(updatedSchemaFields.map((field) => field.name));
  return {
    ...existingConstraint,
    fields: existingConstraint.fields.filter((field) => validFieldNames.has(field.name)),
  };
}

function consolidateFieldsPreserveOrder(
  fields1: Array<ClientSchemaField>,
  fields2: Array<ClientSchemaField>
): Array<ClientSchemaField> {
  const fieldsByName: Record<string, Array<ClientSchemaField>> = groupBy(fields1, (field) => field.name);
  return [
    ...fields1,
    ...fields2.filter((field) => fieldsByName[field.name] == null || fieldsByName[field.name].length === 0),
  ];
}

function updateExistingConstraint(
  constraintSpec: AntibodyEnforcedConstraintSpec,
  existingConstraint: ClientTagSchemaConstraint,
  updatedSchemaFields: Array<ClientSchemaField>
): ClientTagSchemaConstraint {
  const speccedConstraint = constraintSpecToClientTagSchemaConstraint(constraintSpec, updatedSchemaFields);
  const cleanedExistingConstraint = clearInvalidFieldsFromExistingConstraint(
    existingConstraint,
    updatedSchemaFields
  );
  return {
    // For uniqueness, the spec always takes precedence.
    uniqueResidues: speccedConstraint.uniqueResidues,
    uniqueCanonicalSmiles: speccedConstraint.uniqueCanonicalSmiles,
    areUniqueResiduesCaseSensitive: speccedConstraint.areUniqueResiduesCaseSensitive,
    // We specify a minimum set of required constraint fields in the spec, so we merge the existing fields
    // with the specced fields.
    fields: consolidateFieldsPreserveOrder(
      cleanedExistingConstraint.fields as Array<ClientSchemaField>,
      speccedConstraint.fields as Array<ClientSchemaField>
    ),
  };
}

export function computeUpdatedConstraintForEnforcedDataModel(
  schemaSpec: AntibodyEnforcedSchemaSpec | null,
  existingConstraint: ClientTagSchemaConstraint | null,
  updatedSchemaFields: Array<ClientSchemaField>
): ClientTagSchemaConstraint | null {
  if (schemaSpec == null || schemaSpec.enforcedConstraintSpec == null) {
    // If we deselected an antibody component role, we need to clear any fields that were added by that role from the constraint.
    // Otherwise, leave it as is, since we don't have a good way of knowing whether the rest of the constraint
    // was added by us or by the user prior to selecting the role.
    if (existingConstraint != null) {
      return clearInvalidFieldsFromExistingConstraint(existingConstraint, updatedSchemaFields);
    }
    return null;
  }
  if (existingConstraint == null) {
    return constraintSpecToClientTagSchemaConstraint(schemaSpec.enforcedConstraintSpec, updatedSchemaFields);
  }
  return updateExistingConstraint(schemaSpec.enforcedConstraintSpec, existingConstraint, updatedSchemaFields);
}

export function computeUpdatedFieldsForEnforcedDataModel(
  schemaSpec: AntibodyEnforcedSchemaSpec | null,
  registryId: string,
  existingFields: Array<ClientSchemaField>
): Array<ClientSchemaField> {
  if (schemaSpec == null) {
    return removeAntibodyEnforcedFields(existingFields);
  }

  const schemaRole =
    schemaSpec.__typename === 'AntibodyEnforcedRelatedSchemaSpecNode'
      ? schemaSpec.relatedSchemaRole
      : schemaSpec.componentSchemaRole;
  const interfaceId = ANTIBODY_SCHEMA_ROLE_TO_INTERFACE_ID[schemaRole];

  if (!validateDropdownFieldSpecs(schemaSpec, registryId)) {
    notifications.error(
      `Tried to automatically add schema fields for '${ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[interfaceId]}', but could not find all the prerequisite dropdown definitions in this registry. Please ask your tenant admin to run the 'set_up_antibody_dropdown_definitions' script for registry ID '${registryId}', then refresh this page and try again.`
    );
    return removeAntibodyEnforcedFields(existingFields);
  }

  const enforcedFieldReservedNames = new Set(schemaSpec.enforcedFieldSpecs.map((spec) => spec.fieldName));
  const enforcedFieldReservedSystemNames = new Set(
    schemaSpec.enforcedFieldSpecs.map((spec) => spec.systemName)
  );

  const existingFieldsWithoutConflictsOrEnforcedFields = removeAntibodyEnforcedFields(
    existingFields.filter(
      (field) =>
        !enforcedFieldReservedNames.has(field.name) && !enforcedFieldReservedSystemNames.has(field.systemName)
    )
  );

  const enforcedFieldsToPrepend = schemaSpec.enforcedFieldSpecs.map((enforcedFieldSpec) =>
    enforcedFieldSpecToField(interfaceId, enforcedFieldSpec, registryId)
  );

  return [...enforcedFieldsToPrepend, ...existingFieldsWithoutConflictsOrEnforcedFields];
}

const ENFORCED_FIELD_SPECS_FRAGMENT = gql`
  fragment EnforcedFieldSpecsFragment on AntibodyEnforcedSchemaFieldSpecNode {
    fieldName
    systemName
    fieldType
    fieldRole
    isRequired
    isMulti
    dropdown {
      name
      allowedOptions
      compatibleDropdowns {
        id
        registry {
          id
        }
      }
    }
    linkedInterface {
      id
      name
    }
    unit {
      id
      symbol
    }
  }
`;

const ENFORCED_CONSTRAINT_SPEC_FRAGMENT = gql`
  fragment EnforcedConstraintSpecFragment on AntibodyEnforcedConstraintSpecNode {
    enforcedTypeSpecificConstraint {
      constraintType
      isCaseSensitive
    }
    enforcedMinimumFieldDefinitionNames
  }
`;

export const ANTIBODY_COMPONENT_SCHEMA_CONFIGURATION_QUERY: DocumentNode = gql`
  query AntibodyComponentSchemaConfigurationQuery @skipAudit @engComponent(name: ANTIBODIES) {
    antibodyEnforcedSchemaSpecs {
      ... on AntibodyEnforcedRelatedSchemaSpecNode {
        relatedSchemaRole: schemaRole
        enforcedFieldSpecs {
          ...EnforcedFieldSpecsFragment
        }
        enforcedConstraintSpec {
          ...EnforcedConstraintSpecFragment
        }
      }
      ... on AntibodyEnforcedComponentSchemaSpecNode {
        componentSchemaRole: schemaRole
        enforcedFieldSpecs {
          ...EnforcedFieldSpecsFragment
        }
        enforcedConstraintSpec {
          ...EnforcedConstraintSpecFragment
        }
      }
    }
  }
  ${ENFORCED_FIELD_SPECS_FRAGMENT}
  ${ENFORCED_CONSTRAINT_SPEC_FRAGMENT}
`;

/**
 * Dropdown component for configuring antibody component schema interfaces.
 * Displays options for selecting the antibody component type (e.g., AA domain,
 * AA chain, DNA domain, DNA chain, AA variable pair, DNA variable pair) based on the item type.
 */
export function AntibodyComponentSchemaConfiguration({
  disabled,
  itemType,
  onChange,
  value,
  schemaSpecs,
}: AntibodyComponentSchemaConfigurationProps): JSX.Element {
  const handleSelect = (selectedValue: AntibodyComponentSchemaOption): void => {
    analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.SELECTED_ANTIBODY_COMPONENT_SSI_ON_SCHEMA, {
      componentType: selectedValue,
      itemType,
    });
    if (selectedValue == null) {
      onChange(null, null);
      return;
    }
    const expectedRole = ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE[selectedValue];
    const schemaSpec =
      schemaSpecs.find((spec) =>
        spec.__typename === 'AntibodyEnforcedRelatedSchemaSpecNode'
          ? spec.relatedSchemaRole === expectedRole
          : spec.componentSchemaRole === expectedRole
      ) ?? null;
    onChange(selectedValue, schemaSpec);
  };

  const options = getOptionsForItemType(itemType);

  return (
    <Stack spacing="xs" style={{width: '50%'}}>
      <SettingsLabel label="Antibody component type" />
      <SelectDropdown
        menu={
          <SearchableMenu
            optionDatas={options}
            onSelect={handleSelect}
            selectedValue={value}
            width={Menu.WIDTHS.MEDIUM}
          />
        }
        displayName={value != null ? ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[value] : 'None'}
        disabled={disabled}
        alwaysDropDown={true}
        dataTestAttrs={{
          component: 'AntibodyComponentSchemaConfiguration',
          element: 'dropdown',
        }}
      />
    </Stack>
  );
}

export default function QueryingAntibodyComponentSchemaConfiguration({
  disabled,
  itemType,
  onChange,
  value,
}: QueryingAntibodyComponentSchemaConfigurationProps): JSX.Element {
  const enforcedSchemaSpecsResult = useSkippableBenchlingQuery<AntibodyComponentSchemaConfigurationQuery>(
    ANTIBODY_COMPONENT_SCHEMA_CONFIGURATION_QUERY,
    {
      skip: disabled,
    }
  );
  if (enforcedSchemaSpecsResult.type === QueryResultType.PENDING) {
    return <Spinner />;
  }
  if (enforcedSchemaSpecsResult.type === QueryResultType.ERROR) {
    throw new Error(
      `Error querying antibody component schema configuration: ${enforcedSchemaSpecsResult.error}`
    );
  }
  const enforcedSchemaSpecs =
    enforcedSchemaSpecsResult.type === QueryResultType.SKIPPED
      ? []
      : enforcedSchemaSpecsResult.data.antibodyEnforcedSchemaSpecs;
  return (
    <AntibodyComponentSchemaConfiguration
      disabled={disabled}
      itemType={itemType}
      onChange={onChange}
      schemaSpecs={enforcedSchemaSpecs}
      value={value}
    />
  );
}
